import React from 'react';
import PropTypes from 'prop-types';
import { validations } from '@unite-us/app-components';
import { noop } from 'lodash';
import {
  DateField,
  InputField,
  SelectField,
  DurationField,
  RadioField,
} from '@unite-us/ui';
import TimeBasedBillableUnits from 'common/TimeBasedBillableUnits/TimeBasedBillableUnits';
import DateValidationMessages from './DateValidationMessages';

const DATE_SELECTOR_OPTIONS = [
  { label: 'Single Date', value: 'Single Date' },
  { label: 'Date Range', value: 'Date Range' },
];

const AM_PM_SELECTOR_OPTIONS = [
  { label: 'AM', value: 'AM' },
  { label: 'PM', value: 'PM' },
];

const generateTimeSelectOptions = (timeUnit) => {
  const config = {
    hours: { length: 12, startFrom: 1 },
    minutes: { length: 60, startFrom: 0 },
  };

  const { length, startFrom } = config[timeUnit] || {};

  return Array.from({ length }, (_, i) => {
    const value = (startFrom + i).toString().padStart(2, '0');
    return { label: value, value };
  });
};

const TimeBasedDetails = ({
  fields,
  registerField,
  enforceRequiredFields,
  unitAmountCalculationHint,
  remainingAuthorizedAmount,
  canInvoiceAboveRemainingAuthorizedAmount,
  feeScheduleProgramName,
  ignoreSocialCareExpiredDates,
  insuranceDateNonValid,
  insuranceExpired,
  requestedDatesNonValid,
}) => {
  const isDateRange = fields.provided_service.period_of_service?.value === 'Date Range';
  const hasSelectedDate = !!fields.provided_service.service_start_date.value;

  return (
    <div className="flex pb-4 space-x-12">
      <div className="w-1/2">
        <RadioField
          className="col-span-5 lg:col-span-2 px-0"
          field={fields.provided_service.period_of_service}
          id="provided-service-period-of-service"
          label="Period of Service"
          options={DATE_SELECTOR_OPTIONS}
        />
        <div className="col-span-5 lg:col-span-2 px-0">
          {isDateRange ? (
            <DurationField
              id="provided-service-dates"
              label="Service Dates"
              startField={fields.provided_service.service_start_date}
              endField={fields.provided_service.service_end_date}
              inline={false}
              required={isDateRange && enforceRequiredFields}
              validations={isDateRange && enforceRequiredFields ? validations.isRequired : noop}
            />
          ) : (
            <DateField
              field={fields.provided_service.service_start_date}
              id="provided-service-date"
              label="Service Date"
              ref={registerField}
              required={!isDateRange && enforceRequiredFields}
              validations={!isDateRange && enforceRequiredFields ? validations.isRequired : noop}
            />
          )}
        </div>
        <DateValidationMessages
          canInvoiceAboveRemainingAuthorizedAmount={canInvoiceAboveRemainingAuthorizedAmount}
          feeScheduleProgramName={feeScheduleProgramName}
          hasSelectedDate={hasSelectedDate}
          ignoreSocialCareExpiredDates={ignoreSocialCareExpiredDates}
          insuranceDateNonValid={insuranceDateNonValid}
          insuranceExpired={insuranceExpired}
          requestedDatesNonValid={requestedDatesNonValid}
        />
        <label htmlFor="start-time-hour" className="block">Start Time</label>
        <div className="flex items-center gap-2">
          <div className="text-gray-600 w-1/3">
            <SelectField
              id="start-time-hour"
              label=""
              field={fields.provided_service.start_time_hour}
              placeholder="HR"
              type="number"
              options={generateTimeSelectOptions('hours')}
              shouldSort={false}
              ref={registerField}
              className="mb-0"
            />
          </div>
          <div className="h-8 font-bold select-none">:</div>
          <div className="text-gray-600 w-1/3">
            <SelectField
              id="start-time-minute"
              label=""
              field={fields.provided_service.start_time_minute}
              placeholder="MIN"
              type="number"
              options={generateTimeSelectOptions('minutes')}
              shouldSort={false}
              ref={registerField}
              className="mb-0"
            />
          </div>
          <div className="text-gray-600 w-1/3">
            <SelectField
              id="start-time-ampm"
              label=""
              field={fields.provided_service.start_time_ampm}
              options={AM_PM_SELECTOR_OPTIONS}
              placeholder="AM/PM"
              ref={registerField}
              className="mb-0"
            />
          </div>
        </div>
      </div>
      <div className="border-l border-dark-fill-blue border-solid" />
      <div className="w-1/2">
        <div className="mb-6">
          <div className="flex gap-2 items-center">
            <div className="w-2/3">
              <InputField
                id="service-duration"
                label="Service Duration"
                field={fields.provided_service.service_duration}
                placeholder="Enter a duration"
                type="number"
                suffix="Minutes"
                required={enforceRequiredFields}
                ref={registerField}
                validations={enforceRequiredFields ? validations.isRequired : noop}
              />
            </div>
            <div>Minutes</div>
          </div>
          <p className="text-grey text-xs">
            Enter the total time (in minutes) you spent providing services for the client.
          </p>
        </div>
        <div>
          <label htmlFor="start-time-hour" className="block">Billable Units</label>
          <p className="text-dark-grey text-xs">
            <TimeBasedBillableUnits
              unitAmountCalculationHint={unitAmountCalculationHint}
              serviceDuration={fields.provided_service.service_duration?.value}
              remainingAuthorizedAmount={remainingAuthorizedAmount}
            />
          </p>
          <p className="text-grey text-xs">
            Calculated based on service duration, but cannot exceed remaining authorized amount.
          </p>
        </div>
      </div>
    </div>
  );
};

TimeBasedDetails.propTypes = {
  fields: PropTypes.object.isRequired,
  registerField: PropTypes.func.isRequired,
  enforceRequiredFields: PropTypes.bool.isRequired,
  unitAmountCalculationHint: PropTypes.string.isRequired,
  remainingAuthorizedAmount: PropTypes.number,
  canInvoiceAboveRemainingAuthorizedAmount: PropTypes.bool.isRequired,
  feeScheduleProgramName: PropTypes.string.isRequired,
  ignoreSocialCareExpiredDates: PropTypes.bool.isRequired,
  insuranceDateNonValid: PropTypes.bool.isRequired,
  insuranceExpired: PropTypes.bool.isRequired,
  requestedDatesNonValid: PropTypes.bool.isRequired,
};

TimeBasedDetails.defaultProps = {
  remainingAuthorizedAmount: null,
};

export default TimeBasedDetails;
